// apps/client/src/components/help/HelpButton.tsx
// Drops into PageHeader.actions slot. Owns its own drawer state.

import React, { useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import type { PageHelpKey } from '../../config/helpContent';
import { HelpDrawer } from './HelpDrawer';
import { useTrackEvent } from '../../hooks/useTrackEvent';

interface HelpButtonProps {
  pageKey: PageHelpKey;
  /** Visual variant. 'icon' = subtle 36px square button. 'pill' = labelled "Help" button. */
  variant?: 'icon' | 'pill';
}

function deviceClass(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

export const HelpButton: React.FC<HelpButtonProps> = ({
  pageKey,
  variant = 'icon',
}) => {
  const [open, setOpen] = useState(false);
  const openedAtRef = useRef<number | null>(null);
  const { trackEvent } = useTrackEvent();

  const handleOpen = () => {
    setOpen(true);
    openedAtRef.current = Date.now();
    trackEvent('help_opened', {
      pageKey,
      source: 'header_button',
      deviceClass: deviceClass(),
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseTelemetry = (info: { scrolledToBottom: boolean }) => {
    trackEvent('help_closed', {
      pageKey,
      durationMs: openedAtRef.current ? Date.now() - openedAtRef.current : 0,
      scrolledToBottom: info.scrolledToBottom,
    });
    openedAtRef.current = null;
  };

  return (
    <>
      {variant === 'pill' ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Help"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] hover:border-gray-500/30 transition-all duration-150"
        >
          <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
          <span>Help</span>
        </button>
      ) : (
        // Icon variant — matches the PageHeader title icon's circular ring,
        // brand-blue gradient + glow, 2.625rem size, so the two icons form
        // visual book-ends of the header row. Subtle hover scale gives the
        // 3D lift.
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Open help for this page"
          className="inline-flex items-center justify-center w-[2.625rem] h-[2.625rem] rounded-full flex-shrink-0 transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500/50 group"
          style={{
            background:
              'linear-gradient(135deg, rgba(37, 99, 235, 0.20), rgba(37, 99, 235, 0.08))',
            boxShadow:
              '0 0 24px rgba(37, 99, 235, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(37, 99, 235, 0.30)',
          }}
        >
          <HelpCircle
            className="w-5 h-5 text-blue-400 group-hover:text-blue-300 transition-colors"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </button>
      )}

      <HelpDrawer
        open={open}
        onClose={handleClose}
        pageKey={pageKey}
        onCloseTelemetry={handleCloseTelemetry}
        onUseCaseClick={(idx) =>
          trackEvent('help_use_case_clicked', { pageKey, useCaseIndex: idx })
        }
        onRelatedPageClick={(target) =>
          trackEvent('help_related_page_clicked', { pageKey, targetPageKey: target })
        }
      />
    </>
  );
};

export default HelpButton;
